import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setAgent, setUserData } from 'reducers/currentUser/currentUserSlice';
import {
  setWhiteLabelSettings,
  setIsInitialised,
  setSelectedBooking,
  setSelectedQuote,
  setLatestBookings,
  setLatestQuotes,
  setComponentConfig,
  setTextConfig,
  setLoginCredentials
} from 'reducers/ui/uiSlice';
import { getUsersNames } from 'services/CommonService';
import { fetchData } from 'shared/helpers/common';

export default function useInitializeApp() {
  const [isInitializing, setIsInitializing] = useState(true);
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.currentUser);
  const { selectedBooking, selectedQuote, latestBookings, latestQuotes, whiteLabelSettings, componentConfig, isInitialised } =
    useSelector((state) => state.ui);

  const loadAgentData = useCallback(
    async (userDetailsData) => {
      const agentUserId = userDetailsData?.assignees?.[0];

      if (!agentUserId) {
        return;
      }

      try {
        const { data } = await getUsersNames([agentUserId]);
        const agentData = data?.results?.[0] || {};
        dispatch(setAgent(agentData));
      } catch (error) {
        console.error(error);
      }
    },
    [dispatch],
  );

  const initializeApp = useCallback(
    async (loginCredentials, forceReinitialize = false) => {
      if (
        whiteLabelSettings && Object.keys(whiteLabelSettings).length > 0 &&
        userData && Object.keys(userData).length > 0 &&
        componentConfig && Object.keys(componentConfig).length > 0 &&
        isInitialised &&
        !forceReinitialize &&
        (  
          (latestBookings && Object.keys(latestBookings).length > 0) ||  
          (latestQuotes && Object.keys(latestQuotes).length > 0)  
        ) &&
        (  
          (selectedBooking && Object.keys(selectedBooking).length > 0) ||  
          (selectedQuote && Object.keys(selectedQuote).length > 0)  
        )
      ) {
        // Data is already present in Redux, no need to fetch again
        setIsInitializing(false);
        return;
      }

      // Data is not present, fetch it
      const [userDetailsData, { footer = {}, styling = {}, textConfig = {} }, componentConfigData] =
        await Promise.all([
          fetchData('/api/self-service/whoami'),
          fetchData('/api/self-service/config', {}),
          fetchData('/api/self-service/componentConfig', {}),
        ]);

      const referenceQuery = componentConfigData?.multiBookingEnabled ? '' : `?reference=${loginCredentials?.reference}`;

      const bookingData = await fetchData(
        `/api/self-service/composite/qb${referenceQuery}`
      );

      // Dispatch actions to update Redux store
      await loadAgentData(userDetailsData);

      dispatch(setLoginCredentials(loginCredentials));
      dispatch(setSelectedBooking(bookingData?.bookings?.results[0] || []));
      dispatch(setSelectedQuote(bookingData?.quotes?.results[0] || []));
      dispatch(setLatestBookings(bookingData?.bookings?.results || []));
      dispatch(setLatestQuotes(bookingData?.quotes?.results || []));
      dispatch(setWhiteLabelSettings({ ...footer, ...styling }));
      dispatch(setUserData(userDetailsData));
      dispatch(setComponentConfig(componentConfigData));
      dispatch(setTextConfig(textConfig));
      dispatch(setIsInitialised(true));

      setIsInitializing(false);
    },
    [
      dispatch,
      loadAgentData,
      isInitialised,
      selectedBooking,
      selectedQuote,
      latestBookings,
      latestQuotes,
      userData,
      whiteLabelSettings,
      componentConfig,
    ],
  );

  return [isInitializing, initializeApp];
}
